import { useEffect, useState, useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import moment from "moment";
import I18n from "^/config/i18n";
import setLanguage from "^/utils/setLanguage";
import PageHeadBlock from "^/Components/StoriesPreview/PageHeadBlock";
import RecommendedEvents from "./RecommendedEvents";

import { Event } from "../EventPage/EventList";

function EventOverviewPage({ page }) {
  setLanguage(page);
  const pageLength = 12;
  const [pageNumber, setPageNumber] = useState(0);
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(false);
  const currentHour = useMemo(() => moment().startOf("day").toDate(), []);
  const recommendedAll = page.get("recommendedEvents") || [];
  const recommended = recommendedAll.filter(
    (rec) => rec.get("endDate") >= currentHour || rec.get("endDate") === null
  );
  const siteId = page.siteId();
  const lang = page.language();

  const addNewEvent = () => {
    const newEvent = Scrivito.getClass("EventPage").create();
    const fromDate = new Date();
    const untilDate = new Date();
    untilDate.setDate(untilDate.getDate() + 1);

    newEvent.update({
      _siteId: siteId,
      _path: `${page.path()}/${newEvent.id()}`,
      _language: lang,
      date: fromDate,
      endDate: untilDate,
    });
    Scrivito.navigateTo(newEvent);
  };

  const fetchMoreData = useCallback(() => {
    setFetching(true);
    const allQuery = Scrivito.Obj.onSite(siteId)
      .all()
      .and("_objClass", "equals", "EventPage")
      .andNot("endDate", "isLessThan", currentHour)
      .andNot("showInOverviewUntil", "isLessThan", new Date());

    Scrivito.load(() => allQuery.take()).then((searchItems) => {
      setItems((prev) => [...prev, ...searchItems]);
      setPageNumber((prev) => prev + 1);
      setFetching(false);
    });
  }, [currentHour, siteId]);

  const showMoreData = () => {
    setPageNumber((prev) => prev + 1);
  };

  function calculateWeight(event) {
    const today = new Date();
    const daysDifference = Math.ceil(
      (event.get("date") - today) / (1000 * 60 * 60 * 24)
    );
    const duration = Math.ceil(
      (event.get("endDate") - event.get("date")) / (1000 * 60 * 60 * 24)
    );

    let weight = 0;
    // Rule 1: Events next to today are higher rated
    if (daysDifference >= 0 && daysDifference <= 2) {
      weight += 5;
    } else if (daysDifference > 2 && daysDifference <= 7) {
      weight += 1;
    }
    // Rule 2: Longer events have more weight
    weight += Math.log2(duration) / 2;
    // Rule 3: Events in the future are rated down
    if (daysDifference > 0) {
      weight = 0;
    }

    return weight;
  }

  const getItems = () => {
    const weightedItems = [...items];
    weightedItems
      .sort((a, b) => a.get("date") - b.get("date"))
      .sort((item1, item2) => calculateWeight(item2) - calculateWeight(item1));
    return weightedItems.slice(0, pageNumber * pageLength);
  };

  useEffect(() => {
    fetchMoreData();
  }, [fetchMoreData]);

  const itemCount = Math.min(items.length, pageNumber * pageLength);

  return (
    <>
      <PageHeadBlock page={page} />
      <RecommendedEvents
        recommended={recommended}
        language={lang}
        addNewEvent={addNewEvent}
      />

      <div className="container event-overview mt-5">
        <div className="row align-items-stretch">
          {getItems().map((event, index) => (
            <div className="col-12 col-md-4 col-xl-3 mb-4" key={index}>
              <Event event={event} className="--inList" />
            </div>
          ))}
        </div>
      </div>

      {items.length > itemCount && (
        <div className="row">
          <div className="col text-center">
            <button className="btn bg-blue" onClick={showMoreData}>
              {fetching && (
                <i className="las la-spinner la-spin me-1" aria-hidden="true" />
              )}
              {fetching && <span className="sr-only">{I18n.t("loading")}</span>}
              {I18n.t("Load more events")}
            </button>
          </div>
        </div>
      )}

      <Scrivito.ContentTag
        tag="div"
        content={page}
        attribute="body"
        className="mb-3"
      />
    </>
  );
}

Scrivito.provideComponent("EventOverviewPage", EventOverviewPage);
